import React, { memo, useEffect } from 'react'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { blockArrayTypeCheck } from '../../../../utils/blockArrayTypeCheck'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { BlockArrayTypes } from '../../../../types'
import { selectRuleConfigBit } from '../../../../../../../store/selectors/ruleCardSelectors'
import { initRuleEmptyData } from '../../../../../../../store/actions/ruleCardActions'
import { RuleBlockArrayTable } from './RuleBlockArrayTable'
import { RuleChipListBlock } from './RuleChipListBlock'
import { RuleSchedulesContainer } from './RuleSchedulesContainer'
import { RuleCardsContainer } from './RuleCardsContainer'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    schema: any
    name: string
    wrapper?: string
    levelDeep?: number
    isViewOnly?: boolean
    element_id?: string
    inCard?: boolean
    isRuleActive?: boolean
    isParentDisabled?: boolean
}

export const RuleBlockArray = memo(function RuleBlockArray({
    dataPath,
    pathWithElementID,
    schema = {},
    name,
    wrapper,
    levelDeep = 1,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
    isParentDisabled,
}: Props) {
    const dispatch = useAppDispatch()
    const schemaValues: string[] = Object.values(
        schema.children?.children || schema.children
    )
    const model = schema.children?.model
    const componentType: string = blockArrayTypeCheck(
        schemaValues,
        model,
        false
    )
    const path = [...dataPath.split(PATH_JOINER), name]
    const config = useAppSelector(selectRuleConfigBit(path))

    const modifiedPathWithElementID = pathWithElementID
        ? [...(pathWithElementID?.split(PATH_JOINER) ?? []), name]
        : undefined

    useEffect(() => {
        if (config === null || config === undefined) {
            dispatch(initRuleEmptyData({ path, name, schema }))
        }
    }, []) // eslint-disable-line

    if (componentType === BlockArrayTypes.TABLE) {
        return (
            <RuleBlockArrayTable
                dataPath={path.join(PATH_JOINER)}
                config={config}
                schema={schema}
                name={name}
                levelDeep={levelDeep}
                isViewOnly={isViewOnly}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
                pathWithElementID={pathWithElementID}
                isParentDisabled={isParentDisabled}
            />
        )
    }

    if (componentType === BlockArrayTypes.CHIP_LIST) {
        return (
            <RuleChipListBlock
                dataPath={dataPath}
                data={config}
                dataSchema={schema}
                name={name}
                wrapper={wrapper}
                levelDeep={levelDeep}
                element_id={element_id}
                inCard={inCard}
                isRuleActive={isRuleActive}
                pathWithElementID={pathWithElementID}
                isParentDisabled={isParentDisabled}
            />
        )
    }

    if (componentType === BlockArrayTypes.SCHEDULES) {
        return (
            <RuleSchedulesContainer
                dataPath={path.join(PATH_JOINER)}
                schema={schema}
                config={config}
                element_id={element_id}
                pathWithElementID={pathWithElementID}
                wrapper={wrapper}
                levelDeep={levelDeep}
                inCard={inCard}
                isRuleActive={isRuleActive}
                isParentDisabled={isParentDisabled}
                isViewOnly={isViewOnly}
            />
        )
    }

    return (
        <RuleCardsContainer
            dataPath={path.join(PATH_JOINER)}
            config={config || []}
            name={name}
            schema={schema}
            wrapper={wrapper}
            levelDeep={levelDeep}
            isViewOnly={isViewOnly}
            element_id={element_id}
            inCard={inCard}
            isRuleActive={isRuleActive}
            pathWithElementID={modifiedPathWithElementID?.join(PATH_JOINER)}
            isParentDisabled={isParentDisabled}
        />
    )
})
